import _ from 'underscore';

const CAMPAIGN_SETTING_FIELD_HEADERS = {
  DisclaimerAdsEnabled: 'Disclaimer Ads Enabled',
  ChannelOptimizationOptOut: 'Channel Optimization Opt Out',
  AudienceOptimizationOptOut: 'Audience Optimization Opt Out',
  FinalUrlExpansionOptOut: 'Url Expansion Opt Out',
  AutoGeneratedTextOptOut: 'Auto Generated Text Assets Opt Out',
  AutoGeneratedImageOptOut: 'Auto Generated Image Assets Opt Out',
  SearchTermMatchingOptOut: 'Search Term Matching Opt Out',
  CostPerSaleOptOut: 'Cost Per Sale Opt Out',
  NewCustomerAcquisitionGoalId: 'New Customer Acquisition Goal Id',
  NewCustomerAcquisitionBidOnlyMode: 'New Customer Acquisition Bid Only Mode',
  AdditionalConversionValue: 'Additional Conversion Value',
};

const ODATA_TYPE_PREFIX = '#Model.';

function settingTypeKey(setting) {
  const type = setting && setting['@odata.type'];
  if (typeof type !== 'string') {
    return undefined;
  }
  return type.startsWith(ODATA_TYPE_PREFIX) ? type.slice(ODATA_TYPE_PREFIX.length) : type;
}

// AudienceSettings.VerifiedTrackingSettings -> the "Verified Tracking Settings" bulk cell.
// MT's parser (VerifiedTrackingSettingsHelper.TryGetVerifiedTrackingSettingArray) deserializes a
// KeyValuePair<string,string>[][] JSON string, dispatches on the "vendorName" pair, and re-adds the
// per-vendor prefix — so each stored field key (e.g. "IAS_advertiserEntityId") is emitted with the
// "VendorPrefix_" stripped ("advertiserEntityId"). Format matches the ads bulk-edit + UC Brand E2E
// wire shape: [[{"Key":"vendorName","Value":"IAS"},{"Key":"advertiserEntityId","Value":"123"}]].
function stripVendorPrefix(key) {
  const idx = key.indexOf('_');
  return idx >= 0 ? key.slice(idx + 1) : key;
}

function verifiedTrackingDataToObject(data) {
  if (_.isArray(data)) {
    return _.reduce(data, (result, item) => (
      item && !_.isUndefined(item.Key)
        ? { ...result, [item.Key]: item.Value }
        : result
    ), {});
  }
  if (_.isArray(data.Keys) && _.isArray(data.Values)) {
    return _.reduce(data.Keys, (result, key, index) => (
      _.isString(key)
        ? { ...result, [key]: data.Values[index] }
        : result
    ), {});
  }
  return data;
}

function serializeVerifiedTrackingSettings(verifiedTrackingSettings) {
  const serializable = [];
  _.each(verifiedTrackingSettings, (vts) => {
    if (!vts || !vts.VerifiedTrackingVendor) {
      return;
    }
    const dataEntries = _.filter(vts.VerifiedTrackingData || [], d => d && _.isObject(d.Data) && !_.isEmpty(d.Data));
    if (_.isEmpty(dataEntries)) {
      return;
    }
    const entry = [{ Key: 'vendorName', Value: String(vts.VerifiedTrackingVendor) }];
    _.each(dataEntries, (d) => {
      _.each(verifiedTrackingDataToObject(d.Data), (value, key) => {
        entry.push({ Key: stripVendorPrefix(String(key)), Value: String(value) });
      });
    });
    serializable.push(entry);
  });
  if (!serializable.length) {
    return undefined;
  }
  // The bulk-upload-full-mapper path assigns pair values straight into the CSV row, bypassing the
  // writer's per-cell escaping (getCsvValue). This JSON carries commas + quotes, so escape it here
  // (double the quotes, wrap in quotes) or the commas split the row and shift every later column.
  // Matches MT's UnifiedCampaignBrandKitchenSinkBuilder.BuildVerifiedTrackingSettingsBulkUpsertCell.
  const json = JSON.stringify(serializable);
  return `"${json.replace(/"/g, '""')}"`;
}

function campaignSettingsFullMapper(settings) {
  const pairs = [];
  if (!_.isArray(settings)) {
    return pairs;
  }
  _.each(settings, (setting) => {
    if (!_.isObject(setting)) {
      return;
    }
    _.each(CAMPAIGN_SETTING_FIELD_HEADERS, (header, field) => {
      if (!_.has(setting, field) || _.isUndefined(setting[field])) {
        return;
      }
      pairs.push({ key: header, value: setting[field] });
    });
    // UC Brand CampaignSettings that map to a single flat Campaign bulk column. The MT
    // OData BulkUpsert parser (ImportHeadersMicrosoft + CampaignMapper.ProcessX) recognizes
    // these headers and their value formats.
    switch (settingTypeKey(setting)) {
      case 'ReachOptimizationGoalSetting':
        if (!_.isUndefined(setting.OptimizationGoal)) {
          // "MaxImpressions" | "MaxUniqueReach"
          pairs.push({ key: 'Reach Optimization Goal', value: setting.OptimizationGoal });
        }
        break;
      case 'CampaignFrequencyCapSetting':
        if (!_.isUndefined(setting.CapValue) && !_.isUndefined(setting.TimeGranularity)) {
          // "<CapValue>/<TimeGranularity>", e.g. "5/DAY" (granularity HOUR|DAY|WEEK)
          pairs.push({ key: 'Campaign Frequency Cap', value: `${setting.CapValue}/${setting.TimeGranularity}` });
        }
        break;
      case 'ImpressionTrackingSetting':
        if (!_.isUndefined(setting.ImpressionTrackingUrl)) {
          pairs.push({ key: 'Impression Tracking Url', value: setting.ImpressionTrackingUrl });
        }
        break;
      case 'AudienceSettings': {
        const verifiedTrackingCell = serializeVerifiedTrackingSettings(setting.VerifiedTrackingSettings);
        if (!_.isUndefined(verifiedTrackingCell)) {
          pairs.push({ key: 'Verified Tracking Settings', value: verifiedTrackingCell });
        }
        break;
      }
      default:
        break;
    }
  });
  return pairs;
}

export const Campaign = {
  properties: {
    'bulk-upload-type': {
      type: 'string',
      value: 'Campaign',
    },
    Name: {
      'bulk-upload-header': 'Campaign',
    },
    CampaignSubType: {
      'bulk-upload-header': 'Sub Type',
    },
    TrackingUrlTemplate: {
      'bulk-upload-header': 'Tracking Template',
      bulkDeleteValueIfEmpty: true,
    },
    UrlCustomParameters: {
      'bulk-upload-header': 'Custom Parameter',
      'bulk-upload-mapper': function (value) {
        return _.map(value, p => `{_${p.Key}}=${p.Value}`).join('; ');
      },
      bulkDeleteValueIfEmpty: true,
    },
    FinalUrlSuffix: {
      'bulk-upload-header': 'Final Url Suffix',
      bulkDeleteValueIfEmpty: true,
    },
    BudgetId: {
      'bulk-upload-header': 'Budget Id',
    },
    BudgetName: {
      'bulk-upload-header': 'Budget Name',
    },
    TimeZone: {
      'bulk-upload-header': 'Time Zone',
    },
    AdScheduleUseSearcherTimeZone: {
      'bulk-upload-header': 'Ad Schedule Use Searcher Time Zone',
    },
    Languages: {
      filterable: true,
      'bulk-upload-header': 'Language',
      'bulk-upload-mapper': function (value) {
        return value.join(';');
      },
    },
    NativeBidAdjustment: {
      'bulk-upload-header': 'Bid Adjustment',
    },
    MultimediaAdBidAdjustment: {
      'bulk-upload-header': 'Multi Media Ad Bid Adjustment',
    },
    AutomatedCallToActionOptOut: {
      'bulk-upload-header': 'Automated Call To Action Opt Out',
      type: 'boolean',
    },
    CampaignType: {
      'bulk-upload-header': 'Campaign Type',
      'bulk-upload-mapper': function (value) {
        if (value === 'DynamicSearchAds') {
          return 'Dynamic Search';
        }
        return value;
      },
    },
    MarketingObjective: {
      'bulk-upload-header': 'Marketing Objective',
    },
    Channels: {
      'bulk-upload-header': 'Channels',
      'bulk-upload-mapper': function (value) {
        if (_.isArray(value) && value.length > 0) {
          return value.join(';');
        }
        return '';
      },
    },
    SubChannels: {
      'bulk-upload-header': 'Sub Channels',
      'bulk-upload-mapper': function (value) {
        if (_.isArray(value) && value.length > 0) {
          return value.join(';');
        }
        return '';
      },
    },
    // CampaignSettings is a polymorphic array (DisclaimerSetting,
    // UnifiedAutomationSetting, ...). The bulk serializer resolves an array
    // property's sub-schema via getSchemaOfSubAttr -> items.schema, so the
    // flatten full-mapper must live there (deep-merged onto the EDM items.schema).
    CampaignSettings: {
      items: {
        schema: {
          'bulk-upload-full-mapper': campaignSettingsFullMapper,
        },
      },
    },
    CampaignGoal: {
      'bulk-upload-header': 'Campaign Goal',
      'bulk-upload-mapper': function (value) {
        const mapping = {
          MoreCalls: 1,
          MoreVisitsToPhysicalLocation: 2,
          MoreWebsiteActions: 3,
          MoreBrandAwareness: 4,
          MoreSales: 5,
        };

        return mapping[value];
      },
    },
    DomainName: {
      'bulk-upload-header': 'Website',
    },
    LanguageCode: {
      'bulk-upload-header': 'Domain Language',
    },
    Source: {
      'bulk-upload-header': 'Source',
    },
    DynamicDescriptionEnabled: {
      'bulk-upload-header': 'Dynamic Description Enabled',
    },
    AutoGeneratedAssetsEnabled: {
      'bulk-upload-header': 'RSA Auto Generated Assets Enabled',
    },
    IsBroadMatchOnlyCampaign: {
      'bulk-upload-header': 'Is Broad Match Only Campaign',
    },
    TargetSetting: {
      'bulk-upload-header': 'Target Setting',
    },
    DisclaimerAdsEnabled: {
      'bulk-upload-header': 'Disclaimer Ads Enabled',
    },
    BidStrategyId: {
      'bulk-upload-header': 'Bid Strategy Id',
    },
    BidStrategyName: {
      'bulk-upload-header': 'Bid Strategy Name',
    },
    BidStrategyScope: {
      'bulk-upload-header': 'Bid Strategy Scope',
    },
    CashbackMonthlyBudget: {
      'bulk-upload-header': 'Cashback Monthly Budget',
    },
    CashbackPercent: {
      'bulk-upload-header': 'Cashback Percent',
    },
    CashbackScope: {
      'bulk-upload-header': 'Cashback Scope',
    },
    PersonalizedOffersEnabled: {
      'bulk-upload-header': 'Personalized Offers Enabled',
    },
    CouponsEnabled: {
      'bulk-upload-header': 'Personalized Coupons Enabled',
    },
    VerifiedTrackingData: {
      'bulk-upload-header': 'Verified Tracking Settings',
      'bulk-upload-mapper': function (value) {
        if (_.isEmpty(value) || (_.isString(value) && value.length === 0)) {
          // '': two inouts are empty
          // 'error': Only one of id is '', invalid
          return 'delete_value';
        }
        return value;
      },
    },
    FeedLabel: {
      'bulk-upload-header': 'FeedLabel',
    },
    SalesCountry: {
      'bulk-upload-header': 'Country Code',
    },
    ShoppableAdsEnabled: {
      'bulk-upload-header': 'Shoppable Ads Enabled',
      'bulk-upload-mapper': function (value) {
        return value ? 'true' : 'false';
      },
    },
    BrandOnly: {
      'bulk-upload-header': 'Brand Only',
      'bulk-upload-mapper': function (value) {
        return value ? 'true' : 'false';
      },
    },
    LocalInventoryAdsEnabled: {
      'bulk-upload-header': 'Local Inventory Ads Enabled',
      'bulk-upload-mapper': function (value) {
        return value ? 'true' : 'false';
      },
    },
    Priority: {
      'bulk-upload-mapper': function (value) {
        const mapping = {
          Low: 0,
          Medium: 1,
          High: 2,
          Highest: 3,
        };

        return mapping[value];
      },
    },
    ProviderId: {
      'bulk-upload-header': 'Store Id',
    },
    IsLeadGenCampaign: {
      'bulk-upload-header': 'Is Lead Gen Campaign',
    },
    AutoTargetingEnabled: {
      'bulk-upload-header': 'Predictive Targeting Enabled',
      type: 'boolean',
    },
    AutoGeneratedImageOptOut: {
      'bulk-upload-header': 'Auto Generated Image Assets Opt Out',
      type: 'boolean',
    },
    AutoGeneratedTextOptOut: {
      'bulk-upload-header': 'Auto Generated Text Assets Opt Out',
      type: 'boolean',
    },
    AISearchEnabled: {
      'bulk-upload-header': 'AI Search Enabled',
      type: 'boolean',
    },
    SearchTermMatchingOptOut: {
      'bulk-upload-header': 'Search Term Matching Opt Out',
      type: 'boolean',
    },
    FinalUrlExpansionOptOut: {
      'bulk-upload-header': 'Url Expansion Opt Out',
      type: 'boolean',
    },
    TermExclusions: {
      'bulk-upload-header': 'Term Exclusions',
      'bulk-upload-mapper': function (value) {
        if (value === undefined) return '';
        if (_.isArray(value) && value.length > 0) {
          return JSON.stringify(value.map(item => (typeof item === 'string' ? { Text: item } : item)));
        }
        return 'delete_value';
      },
    },
    MessagingRestrictions: {
      'bulk-upload-header': 'Messaging Restrictions',
      'bulk-upload-mapper': function (value) {
        if (value === undefined) return '';
        if (_.isArray(value) && value.length > 0) {
          return JSON.stringify(value.map(item => (typeof item === 'string' ? { Text: item } : item)));
        }
        return 'delete_value';
      },
    },
    CostPerSaleOptOut: {
      'bulk-upload-header': 'Cost Per Sale Opt Out',
      type: 'boolean',
    },
    AppStore: {
      'bulk-upload-header': 'App Store',
      type: 'string',
    },
    AppId: {
      'bulk-upload-header': 'App Id',
      type: 'string',
    },
    PageFeedIds: {
      type: 'string',
      'bulk-upload-header': 'Page Feed Ids',
      'bulk-upload-mapper': function (value) {
        if (_.isArray(value)) {
          return _.isEmpty(value) ? 'delete_value' : value.join(';');
        }
        return '';
      },
    },
    DisplayUrlMode: {
      type: 'string',
      'bulk-upload-header': 'Vanity Pharma Display URL Mode',
    },
    WebsiteDescription: {
      type: 'string',
      'bulk-upload-header': 'Vanity Pharma Website Description',
    },
    IsDealCampaign: {
      'bulk-upload-header': 'Is Deal Campaign',
      type: 'boolean',
    },
    CallToActionOptOut: {
      'bulk-upload-header': 'Call To Action Opt Out',
      type: 'boolean',
    },
    StartDate: {
      'bulk-upload-header': 'Start Date',
    },
    EndDate: {
      'bulk-upload-header': 'End Date',
    },
    UseCampaignLevelDates: {
      'bulk-upload-header': 'Use Campaign Level Dates',
    },
    NewCustomerAcquisitionGoalId: {
      'bulk-upload-header': 'New Customer Acquisition Goal Id',
    },
    NewCustomerAcquisitionBidOnlyMode: {
      'bulk-upload-header': 'New Customer Acquisition Bid Only Mode',
      type: 'boolean',
    },
    AdditionalConversionValue: {
      'bulk-upload-header': 'Additional Conversion Value',
    },
  },
};
